'use client';

import React, { useState, useEffect } from 'react';
// import profilePlaceholder from '../assets/images/profile-placeholder.svg';
import UploadPhotoModal from '../../components/modals/uploadPhotoModal';
import ChangePasswordModal from '../../components/modals/changePasswordModal';
import profileUpdatedModalIcon from '../../assets/icons/profile-updated-modal-icon.svg';
import passwordChangeSuccessIcon from '../../assets/icons/passwordSetSuccess.svg';
import { useNavigate } from 'react-router-dom';
import { Toast } from '../../components/shared/Toast';
import { useAuthContext } from '../../hooks/useAuth';
import { AdminService } from '../../services';
import Modal from '../../components/modals/modal';
import EditProfileModal from '../../components/modals/editProfileModal';
import Button from '../../components/shared/Button';

const styles = {
	settingsHeading: 'text-dark-blue font-bold text-main mt-10',
	section: 'bg-white mt-[50px] mb-2.5 md:px-[50px] px-0 border border-lightGrey',
	profileTopSection:
		'flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[46px] pt-[46px] md:px-0 px-3',
	buttonStyle: 'lg:!w-[180px] lg:!h-[45px] rounded-lg !font-semibold !text-sm',
	profilePhotoSection:
		'w-24 h-24 p-4 justify-center items-center rounded-85p border-075p border-lightGrey mr-3',
	inputSection: 'grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mt-7 mb-16 md:px-0 px-3',
	label: 'block text-gray text-sm font-medium mb-2',
	input:
		'block w-full border border-lightGrey rounded-lg px-4 py-3 text-dark-blue placeholder-gray focus:outline-none focus:border-purple'
};

export default function AdminSettingsPage() {
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
	const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
	const [isProfileUpdatedModalOpen, setIsProfileUpdatedModalOpen] = useState(false);
	const [passwordChangeSuccessMessage, setPasswordChangeSuccessMessage] = useState(''); // New state for password change success message
	const navigate = useNavigate();

	// Get user data from authentication context
	const { user, getUserPicture, updateUserData, isLoading, logout } = useAuthContext();

	// Initialize user profile with data from auth context
	const [userProfile, setUserProfile] = useState({
		firstName: user?.firstName || '',
		lastName: user?.lastName || '',
		email: user?.email || '',
		photo: getUserPicture()
	});

	// Update user profile when user data changes (only when user object changes)
	useEffect(() => {
		if (user) {
			setUserProfile({
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				email: user.email || '',
				photo: user.pictureUrl || getUserPicture()
			});
		}
	}, [user, getUserPicture]);

	const handleUploadDrive = () => setIsUploadModalOpen(false);
	const handleUploadDevice = () => setIsUploadModalOpen(false);
	const closeProfileUpdatedOrPasswordChangedModal = () => {
		setIsProfileUpdatedModalOpen(false);
		if (passwordChangeSuccessMessage) {
			logout();
			navigate('/admin/login', { replace: true });
		}
	};

	// Updated handler for password change success
	const handleChangePassword = (message) => {
		setIsChangePasswordOpen(false); // Close the change password modal
		setPasswordChangeSuccessMessage(message); // Set the custom success message
		setIsProfileUpdatedModalOpen(true); // Open the profile updated modal
	};

	const handleSaveProfile = async (updatedProfile) => {
		try {
			// Prepare profile data for API - only include fields that have values
			const profileData = {};

			// Only add fields that have values and are different from current user data
			if (updatedProfile.firstName && updatedProfile.firstName.trim() !== '') {
				profileData.firstName = updatedProfile.firstName.trim();
			}
			if (updatedProfile.lastName && updatedProfile.lastName.trim() !== '') {
				profileData.lastName = updatedProfile.lastName.trim();
			}
			if (updatedProfile.profileImage) {
				profileData.profileImage = updatedProfile.profileImage;
			}

			// Check if at least one field is provided
			if (Object.keys(profileData).length === 0) {
				Toast.error('Please provide at least one field to update');
				return;
			}

			// Call the update profile API
			const response = await AdminService.updateProfile(user.userId, profileData);

			if (response.status === 200 && response.user) {
				// Update auth context with new customer data
				updateUserData(response.user);

				// Show success toast
				// Toast.success(response.message || 'Profile updated successfully');

				// Close modal and show confirmation
				setIsEditProfileModalOpen(false);
				setIsProfileUpdatedModalOpen(true);
			} else {
				// Handle error
				const errorMessage = response.message || 'Failed to update profile';
				Toast.error(errorMessage);
			}
		} catch (error) {
			Toast.error('Network error. Please try again.');
		}
	};

	// Show loading state while auth is initializing
	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-dark-blue mx-auto"></div>
					<p className="mt-4 text-dark-blue">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="">
			<h4 className={styles.settingsHeading}>Settings</h4>
			<div className={styles.section}>
				<div className={styles.profileTopSection}>
					<h2 className="text-25 font-semibold text-dark-blue">Profile Information</h2>
					<div className="flex space-x-2">
						<Button
							className={styles.buttonStyle}
							variant="outline-gradient-text"
							onClick={() => setIsChangePasswordOpen(true)}
						>
							Change Password
						</Button>
						<Button className={styles.buttonStyle} onClick={() => setIsEditProfileModalOpen(true)}>
							Edit Profile
						</Button>
					</div>
				</div>

				<div className="flex items-center md:px-0 px-3">
					<div className={styles.profilePhotoSection}>
						<img src={userProfile.photo} alt="Profile" className="w-16 h-16 rounded-full" />
					</div>
					<div>
						<p className="text-gray font-normal text-base">Profile Photo</p>
						{/* <Button
							className="!py-2.5 !px-2.5 !rounded-lg !font-normal !text-xs"
							variant="outline-gradient-text"
							onClick={() => setIsUploadModalOpen(true)}
						>
							Upload photo
						</Button> */}
					</div>
				</div>

				<div className={styles.inputSection}>
					<div>
						<label htmlFor="first-name" className={styles.label}>
							First Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="first-name"
							name="first-name"
							value={userProfile.firstName}
							readOnly
							className={styles.input}
						/>
					</div>

					<div>
						<label htmlFor="last-name" className={styles.label}>
							Last Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="last-name"
							name="last-name"
							value={userProfile.lastName}
							readOnly
							className={styles.input}
						/>
					</div>

					<div className="sm:col-span-2">
						<label htmlFor="email-address" className={styles.label}>
							Email Address  <span className="text-red-500">*</span>
						</label>
						<input
							type="email"
							id="email-address"
							name="email-address"
							value={userProfile.email}
							readOnly
							className={styles.input}
						/>
					</div>

					{/* <div>
						<label htmlFor="password" className={styles.label}>
							Password
						</label>
						<input
							type="password"
							id="password"
							name="password"
							value="••••••••••••"
							readOnly
							className={styles.input}
						/>
					</div> */}
				</div>
			</div>

			{/* Upload Photo Modal */}
			<UploadPhotoModal
				isOpen={isUploadModalOpen}
				onClose={() => setIsUploadModalOpen(false)}
				onUploadDrive={handleUploadDrive}
				onUploadDevice={handleUploadDevice}
			/>

			{/* Change Password Modal */}
			<ChangePasswordModal
				isOpen={isChangePasswordOpen}
				onClose={() => setIsChangePasswordOpen(false)}
				onChangePassword={handleChangePassword}
			/>

			{/* Edit Profile Modal */}
			<EditProfileModal
				isOpen={isEditProfileModalOpen}
				onClose={() => setIsEditProfileModalOpen(false)}
				userProfile={userProfile}
				onSaveProfile={handleSaveProfile}
			/>

			{/* Profile Updated Confirmation Modal */}
			<Modal
				isOpen={isProfileUpdatedModalOpen}
				onClose={() => {
					setIsProfileUpdatedModalOpen(false);
					setPasswordChangeSuccessMessage(''); // Clear message on close
				}}
				icon={
					<img
						src={passwordChangeSuccessMessage ? passwordChangeSuccessIcon : profileUpdatedModalIcon}
						alt={passwordChangeSuccessMessage ? 'Password Changed' : 'Profile Updated'}
						className="mx-auto"
					/>
				}
				heading={
					passwordChangeSuccessMessage ? 'Password changed successfully' : 'Profile setting updated'
				}
				description={
					passwordChangeSuccessMessage ||
					'Your account profile has been updated.'
				}
				buttonText="Close"
				onButtonClick={() => closeProfileUpdatedOrPasswordChangedModal()}
			/>
		</div>
	);
}
